import React, { useMemo, useRef, useState } from 'react';
import { ArrowRight, CheckCircle2, ChevronRight, Download, Eye, FileText, Mail, Printer, Send, Trash2, X } from 'lucide-react';
import { Autocomplete, Box, Button, Dialog, IconButton, MenuItem, TextField, Typography, CircularProgress, Select, FormControl, type SelectChangeEvent } from '@mui/material';
import SearchableClientDropdown from '@/components/shared/SearchableClientDropdown';
import type { Client } from '@/types/client';
import { addDays, addMonths, format, isValid, parse, startOfDay } from 'date-fns';
import { createClientInvoice, getClientInvoice, getGeneratedClientInvoiceId, getNutritionistInvoiceContext, resendInvoiceEmail, updateInvoiceStatus, type CreateInvoicePayload, type Invoice, type InvoiceContextPlan, type NutritionistInvoiceContext } from '@/lib/api/invoices';
import { getClientById } from '@/lib/api/clients';
import { getNutritionistConsultationTypes, type NutritionistConsultationType } from '@/lib/api/consultationTypes';
import { useAuth } from '@/hooks/useAuth';
import {
  downloadInvoicePdf,
  getInvoiceClinicLogoUrl,
  getInvoiceShowLogoOnInvoice,
  printInvoicePdf,
  type InvoicePdfPayload,
  withInvoiceClinicLogoDataUrl,
} from '@/utils/invoicePdfExport';
import InvoiceStatusConfirmDialog from '@/components/dashboard/InvoiceStatusConfirmDialog';
import InvoiceLogoHeader from '@/components/dashboard/InvoiceLogoHeader';
import { formatInvoiceCurrencyAmount, getInvoiceCurrencyBadgeLabel } from '@/utils/invoiceCurrency';
import { ClientNameLink } from '@/components/clients/ClientNameLink';
import InlineFeedback from '@/components/shared/InlineFeedback';

interface CreateInvoiceFlowDialogProps {
  open: boolean;
  onClose: () => void;
  initialClientId?: string;
  /** Pre-fill client name/email before the client record exists (e.g. add-client onboarding). */
  initialClientSnapshot?: Partial<Client>;
  initialInvoice?: Invoice | null;
  /** Called after invoice status is updated (e.g. marked paid) so lists can refetch */
  onInvoiceUpdated?: () => void;
  /**
   * When true, Invoice #, Client, Issue Date, and Due Date are read-only (e.g. client-details create flow).
   * Defaults to locking whenever `initialClientId` is set; set explicitly if you need a different rule.
   */
  lockInvoiceMetaFields?: boolean;
  /**
   * Skip POST /client-invoices until the parent supplies a client id (onboarding before client creation).
   */
  deferInvoiceCreation?: boolean;
  /** Fired when `deferInvoiceCreation` is true and the user saves the invoice draft. */
  onInvoiceDraftSave?: (payload: CreateInvoicePayload) => void | Promise<void>;
  /** Restore line items and fields when reopening a deferred onboarding invoice. */
  initialFormDraft?: InvoiceFormDraft | null;
  /** Persist in-progress form when the dialog closes (deferred onboarding only). */
  onFormDraftSave?: (draft: InvoiceFormDraft) => void;
}

type CreateInvoiceStep = 'details' | 'preview' | 'done' | 'invoice';
type StatusSelectValue = 'due' | 'overdue' | 'paid' | 'discarded';
type SelectedInvoiceClient = Partial<Client> & { name?: string };

interface EditableLineItem {
  id: number;
  service: string;
  quantity: string;
  unitPrice: string;
  selectedPlanId?: string;
}

/** In-progress invoice form state (e.g. add-client before POST /clients). */
export type InvoiceFormDraft = {
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  subscriptionStartDate: string;
  subscriptionEndDate: string;
  taxRate: string;
  discountAmountInput: string;
  notes: string;
  sendByEmailEnabled: boolean;
  lineItems: EditableLineItem[];
  nextLineItemId: number;
  invoiceCurrency: string | null;
};

/** Must match all display formatting / parsing for issue & due in this flow */
const INVOICE_DISPLAY_DATE_FORMAT = 'd MMM yyyy';
const INVOICE_INPUT_DATE_FORMAT = 'yyyy-MM-dd';

/** Parse UI date string (e.g. "14 Apr 2026"); avoids unreliable `new Date(string)` parsing. */
function parseInvoiceDisplayDate(value: string): Date {
  const trimmed = value.trim();
  const parsed = parse(trimmed, INVOICE_DISPLAY_DATE_FORMAT, new Date());
  if (!isValid(parsed)) {
    throw new Error(`Invalid invoice date: ${value}`);
  }
  return startOfDay(parsed);
}

function formatInvoiceApiDate(value: string | null | undefined): string {
  if (!value) return '';
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? '' : format(parsed, INVOICE_DISPLAY_DATE_FORMAT);
}

function formatInvoiceInputDate(value: string | null | undefined): string {
  if (!value) return '';
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? '' : format(parsed, INVOICE_INPUT_DATE_FORMAT);
}

function parseInvoiceInputDate(value: string): Date {
  const trimmed = value.trim();
  const parsed = parse(trimmed, INVOICE_INPUT_DATE_FORMAT, new Date());
  if (!isValid(parsed)) {
    throw new Error(`Invalid subscription date: ${value}`);
  }
  return startOfDay(parsed);
}

function getDefaultSubscriptionDates(referenceDay: Date = startOfDay(new Date())): {
  start: string;
  end: string;
} {
  return {
    start: format(referenceDay, INVOICE_INPUT_DATE_FORMAT),
    end: format(addMonths(referenceDay, 1), INVOICE_INPUT_DATE_FORMAT),
  };
}

function formatSubscriptionPeriodLabel(startValue: string, endValue: string): string {
  try {
    const start = parseInvoiceInputDate(startValue);
    const end = parseInvoiceInputDate(endValue);
    return `${format(start, INVOICE_DISPLAY_DATE_FORMAT)} – ${format(end, INVOICE_DISPLAY_DATE_FORMAT)}`;
  } catch {
    return '—';
  }
}

/** ISO for API: calendar day at 12:00 UTC so the stored calendar date matches what the user sees locally. */
function calendarDateToIsoForApi(d: Date): string {
  const y = d.getFullYear();
  const m = d.getMonth();
  const day = d.getDate();
  return new Date(Date.UTC(y, m, day, 12, 0, 0)).toISOString();
}
const DEFAULT_DISCOUNT = '';
const DEFAULT_NOTES = '';
const LIST_MARK_PAID_PAYMENT_METHOD = 'upi';
const EMPTY_LINE_ITEM_OPTIONS_MESSAGE =
  'No consultation types, programs, or packages are available yet. Add them in Nutritionist Profile Settings to show them here.';
const DEFAULT_TAX_RATE_OPTIONS = Array.from({ length: 51 }, (_, index) => String(index));
const invoiceRequiredMarkSx = { color: '#ea5455', ml: 0.25 } as const;
const invoiceFieldLabelSx = {
  color: '#9ca3af',
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  mb: 0.5,
} as const;

const createLineItemFromPlan = (plan: InvoiceContextPlan, id: number): EditableLineItem => ({
  id,
  service: plan.name,
  quantity: '1',
  unitPrice: plan.totalAmount === null ? '' : String(plan.totalAmount),
  selectedPlanId: plan.id || undefined,
});

const createEmptyLineItem = (id: number): EditableLineItem => ({
  id,
  service: '',
  quantity: '1',
  unitPrice: '',
});

const createLineItemPlanFromConsultationType = (
  consultationType: NutritionistConsultationType,
  context: NutritionistInvoiceContext
): InvoiceContextPlan => ({
  id: `consultation:${consultationType.id}`,
  name: consultationType.name,
  totalAmount: consultationType.rate,
  nutritionistCountry: context.nutritionistCountry,
  nutritionistCurrency: context.nutritionistCurrency || context.currency,
});

const buildLineItemOptionsWithConsultationTypes = (
  context: NutritionistInvoiceContext,
  consultationTypes: NutritionistConsultationType[] | null
): InvoiceContextPlan[] => {
  const consultationOptions = Array.isArray(consultationTypes)
    ? consultationTypes
        .filter((consultationType) => consultationType.isActive && consultationType.id && consultationType.name)
        .map((consultationType) => createLineItemPlanFromConsultationType(consultationType, context))
    : [];

  const byId = new Map<string, InvoiceContextPlan>();
  [...consultationOptions, ...context.plans].forEach((plan) => {
    if (!plan.id || byId.has(plan.id)) return;
    byId.set(plan.id, plan);
  });

  return Array.from(byId.values());
};

const parseNumericValue = (value: string): number => {
  const sanitizedValue = value.replace(/[^0-9.]/g, '');
  if (!sanitizedValue) return 0;

  const parsedValue = Number.parseFloat(sanitizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const normalizeCurrencyCode = (value: string | null | undefined): string => {
  return (value || '').trim().toUpperCase();
};

const getCurrencyPrefix = (currency: string | null): string => {
  if (!currency) return '';
  const normalized = normalizeCurrencyCode(currency);
  return normalized ? getInvoiceCurrencyBadgeLabel(normalized) : '';
};

const formatCurrency = (value: number, currency: string | null): string => {
  const normalizedCurrency = normalizeCurrencyCode(currency);
  const safeValue = Math.max(0, value);

  if (!normalizedCurrency) {
    return safeValue.toFixed(2);
  }

  return formatInvoiceCurrencyAmount(safeValue, normalizedCurrency);
};

const formatRateLabel = (value: number): string => {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/\.?0+$/, '');
};

const formatPlanPriceLabel = (plan: InvoiceContextPlan): string =>
  plan.totalAmount === null
    ? 'Price not set'
    : formatCurrency(plan.totalAmount, plan.nutritionistCurrency);

const getApiErrorMessage = (error: unknown): string => {
  const maybeObj = error as {
    response?: { data?: { error?: string; message?: string } };
    message?: string;
  };
  return (
    maybeObj?.response?.data?.error ||
    maybeObj?.response?.data?.message ||
    maybeObj?.message ||
    'Could not update invoice status'
  );
};

const normalizeStatusForSelect = (status?: string): StatusSelectValue => {
  const s = (status || '').toLowerCase();
  if (s === 'paid') return 'paid';
  if (s === 'discarded' || s === 'archived') return 'discarded';
  if (s === 'overdue') return 'overdue';
  return 'due';
};

const mapUiStatusToApiStatus = (value: StatusSelectValue): string => {
  if (value === 'due') return 'sent';
  return value;
};

const statusLabel = (value: StatusSelectValue): string => {
  if (value === 'paid') return 'Paid';
  if (value === 'overdue') return 'Overdue';
  if (value === 'discarded') return 'Discarded';
  return 'Due';
};

const statusColor = (value: StatusSelectValue): string => {
  if (value === 'paid') return '#28c76f';
  if (value === 'overdue') return '#ea5455';
  if (value === 'discarded') return '#64748b';
  return '#ea5455';
};

const invoiceEmailTextSx = {
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
};

const filledDisabledTextFieldSx = {
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: '#111827',
    color: '#111827',
    opacity: 1,
  },
  '& .MuiSelect-select.Mui-disabled': {
    WebkitTextFillColor: '#111827',
    color: '#111827',
    opacity: 1,
  },
  '& .MuiInputBase-root.Mui-disabled': {
    bgcolor: '#fff',
  },
  '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderColor: '#d1d5db',
  },
};

const CreateInvoiceFlowDialog: React.FC<CreateInvoiceFlowDialogProps> = ({
  open,
  onClose,
  initialClientId,
  initialClientSnapshot,
  initialInvoice,
  onInvoiceUpdated,
  lockInvoiceMetaFields,
  deferInvoiceCreation = false,
  onInvoiceDraftSave,
  initialFormDraft,
  onFormDraftSave,
}) => {
  const [step, setStep] = useState<CreateInvoiceStep>('details');
  const [selectedClientId, setSelectedClientId] = useState(initialClientId || '');
  const [selectedClient, setSelectedClient] = useState<SelectedInvoiceClient | null>(null);
  const [sendByEmailEnabled, setSendByEmailEnabled] = useState(true);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [subscriptionStartDate, setSubscriptionStartDate] = useState('');
  const [subscriptionEndDate, setSubscriptionEndDate] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [discountAmountInput, setDiscountAmountInput] = useState(DEFAULT_DISCOUNT);
  const [invoiceDueDays, setInvoiceDueDays] = useState<number | null>(null);
  const [invoiceCurrency, setInvoiceCurrency] = useState<string | null>(null);
  const [invoiceClinicLogoUrl, setInvoiceClinicLogoUrl] = useState('');
  const [invoiceShowClinicLogo, setInvoiceShowClinicLogo] = useState(true);
  const [invoiceContextPlans, setInvoiceContextPlans] = useState<InvoiceContextPlan[]>([]);
  const [isInvoiceContextLoading, setIsInvoiceContextLoading] = useState(false);
  const [isSubscriptionDefaultsLoading, setIsSubscriptionDefaultsLoading] = useState(false);
  const [invoiceContextError, setInvoiceContextError] = useState<string | null>(null);
  const [notes, setNotes] = useState(DEFAULT_NOTES);
  const [lineItems, setLineItems] = useState<EditableLineItem[]>([]);
  const [nextLineItemId, setNextLineItemId] = useState(2);
  const [hasGenerateAttempted, setHasGenerateAttempted] = useState(false);
  const { user } = useAuth();
  const [isCreating, setIsCreating] = useState(false);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [isInvoicePdfBusy, setIsInvoicePdfBusy] = useState(false);
  const [liveInvoice, setLiveInvoice] = useState<Invoice | null>(null);
  const [hasCreatedInvoice, setHasCreatedInvoice] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<StatusSelectValue | null>(null);
  const [isUpdatingInvoiceStatus, setIsUpdatingInvoiceStatus] = useState(false);
  const [invoiceFeedback, setInvoiceFeedback] = useState<{
    variant: 'error' | 'warning' | 'success' | 'info';
    title?: string;
    message: string;
  } | null>(null);
  const isInvoiceMetaLocked =
    lockInvoiceMetaFields ?? (Boolean(initialClientId) || Boolean(initialClientSnapshot));
  const isDeferredOnboardingFlow = deferInvoiceCreation && Boolean(initialClientSnapshot);
  const wasOpenRef = useRef(false);
  const openRef = useRef(open);
  const lineItemsRef = useRef(lineItems);
  const loadedSubscriptionForClientRef = useRef<string | null>(null);
  openRef.current = open;
  lineItemsRef.current = lineItems;

  const invoiceRef = liveInvoice ?? initialInvoice;
  const invoiceContextClinicLogoUrl = invoiceShowClinicLogo ? invoiceClinicLogoUrl.trim() : '';
  const invoiceSnapshotClinicLogoUrl = getInvoiceClinicLogoUrl(invoiceRef, '', invoiceShowClinicLogo);
  const currentClinicLogoUrl = hasCreatedInvoice
    ? invoiceContextClinicLogoUrl || invoiceSnapshotClinicLogoUrl
    : invoiceSnapshotClinicLogoUrl || invoiceContextClinicLogoUrl;
  const displayCurrency = normalizeCurrencyCode(invoiceRef?.currency || invoiceCurrency || '');
  const displayCurrencyPrefix = getCurrencyPrefix(displayCurrency);
  const fromName = invoiceRef?.nutritionistSnapshot?.name || invoiceRef?.nutritionistSnapshot?.fullName || user?.fullName || 'NutriCare Practice';
  const fromEmail = invoiceRef?.nutritionistSnapshot?.email || user?.email || '';

  const handleResendEmail = async () => {
    if (!invoiceRef?.id) return;

    setIsResendingEmail(true);
    try {
      await resendInvoiceEmail(invoiceRef.id);
      setInvoiceFeedback({
        variant: 'success',
        title: 'Invoice email sent',
        message: 'The invoice email was sent successfully.',
      });
    } catch (error) {
      console.error('Failed to resend invoice email:', error);
      setInvoiceFeedback({
        variant: 'error',
        title: 'Failed to send invoice email',
        message: getApiErrorMessage(error),
      });
    } finally {
      setIsResendingEmail(false);
    }
  };

  const applyInvoiceContextDefaults = React.useCallback((
    context: NutritionistInvoiceContext,
    lineItemOptions: InvoiceContextPlan[] = context.plans,
    options?: { skipLineItems?: boolean }
  ) => {
    if (!Number.isFinite(context.paymentDueDays) || context.paymentDueDays < 0) {
      throw new Error('Invoice context returned an invalid paymentDueDays value');
    }
    if (!Number.isFinite(context.vatRate) || context.vatRate < 0) {
      throw new Error('Invoice context returned an invalid vatRate value');
    }

    const currency = normalizeCurrencyCode(context.currency);
    if (!currency) {
      throw new Error('Invoice context returned an empty currency');
    }

    const issueDay = startOfDay(new Date());

    setInvoiceDueDays(context.paymentDueDays);
    setInvoiceCurrency(currency);
    setInvoiceShowClinicLogo(context.showLogoOnInvoice);
    setInvoiceClinicLogoUrl(context.showLogoOnInvoice ? context.clinicLogoUrl || '' : '');
    setInvoiceContextPlans(lineItemOptions);
    setInvoiceContextError(null);
    setTaxRate(String(context.vatRate));
    setIssueDate(format(issueDay, INVOICE_DISPLAY_DATE_FORMAT));
    setDueDate(format(addDays(issueDay, context.paymentDueDays), INVOICE_DISPLAY_DATE_FORMAT));
    if (!options?.skipLineItems) {
      setLineItems((currentLineItems) => {
        const hasUserLineItems = currentLineItems.some(
          (lineItem) => Boolean(lineItem.selectedPlanId) || lineItem.service.trim().length > 0
        );
        if (hasUserLineItems) return currentLineItems;
        return lineItemOptions.length > 0 ? [createEmptyLineItem(1)] : [];
      });
      setNextLineItemId((currentValue) => (currentValue > 1 ? currentValue : 2));
    }
  }, []);

  const captureFormDraft = (): InvoiceFormDraft => ({
    invoiceNumber,
    issueDate,
    dueDate,
    subscriptionStartDate,
    subscriptionEndDate,
    taxRate,
    discountAmountInput,
    notes,
    sendByEmailEnabled,
    lineItems: lineItemsRef.current,
    nextLineItemId,
    invoiceCurrency,
  });

  const restoreFormDraft = (draft: InvoiceFormDraft, options?: { preserveLineItems?: boolean }) => {
    setInvoiceNumber(draft.invoiceNumber);
    setIssueDate(draft.issueDate);
    setDueDate(draft.dueDate);
    const defaultSubscriptionDates = getDefaultSubscriptionDates();
    setSubscriptionStartDate(draft.subscriptionStartDate || defaultSubscriptionDates.start);
    setSubscriptionEndDate(draft.subscriptionEndDate || defaultSubscriptionDates.end);
    setTaxRate(draft.taxRate);
    setDiscountAmountInput(draft.discountAmountInput);
    setNotes(draft.notes);
    setSendByEmailEnabled(draft.sendByEmailEnabled);
    setInvoiceCurrency(draft.invoiceCurrency);

    if (options?.preserveLineItems) return;

    setLineItems(draft.lineItems.length > 0 ? draft.lineItems : [createEmptyLineItem(1)]);
    setNextLineItemId(Math.max(2, draft.nextLineItemId));
  };

  const populateInvoiceData = (
    invoiceToUse: Invoice,
    options: { preserveClinicLogoUrl?: boolean } = {}
  ) => {
    setInvoiceNumber(invoiceToUse.invoiceNo || '');
    setInvoiceCurrency(normalizeCurrencyCode(invoiceToUse.currency));
    const shouldShowInvoiceLogo = getInvoiceShowLogoOnInvoice(invoiceToUse, invoiceShowClinicLogo);
    setInvoiceShowClinicLogo(shouldShowInvoiceLogo);
    if (!options.preserveClinicLogoUrl) {
      const invoiceLogoUrl = getInvoiceClinicLogoUrl(invoiceToUse, invoiceClinicLogoUrl, shouldShowInvoiceLogo);
      setInvoiceClinicLogoUrl(invoiceLogoUrl || '');
    }

    setIssueDate(formatInvoiceApiDate(invoiceToUse.issueDate || invoiceToUse.createdAt));
    setDueDate(formatInvoiceApiDate(invoiceToUse.dueDate));
    setSubscriptionStartDate(formatInvoiceInputDate(invoiceToUse.subscriptionStartDate));
    setSubscriptionEndDate(formatInvoiceInputDate(invoiceToUse.subscriptionEndDate));

    if (invoiceToUse.clientId) {
      setSelectedClientId(invoiceToUse.clientId);
    }

    if (invoiceToUse.clientSnapshot) {
      setSelectedClient(invoiceToUse.clientSnapshot);
    }

    setTaxRate(invoiceToUse.taxPct === null || invoiceToUse.taxPct === undefined ? '' : String(invoiceToUse.taxPct));
    setDiscountAmountInput(invoiceToUse.discount === null || invoiceToUse.discount === undefined ? '' : String(invoiceToUse.discount));
    setNotes(invoiceToUse.notes || '');

    if (invoiceToUse.lineItems && invoiceToUse.lineItems.length > 0) {
      setLineItems(invoiceToUse.lineItems.map((item, idx) => ({
        id: idx + 1,
        service: item.description,
        quantity: String(item.qty),
        unitPrice: String(item.unitPrice)
      })));
      setNextLineItemId(invoiceToUse.lineItems.length + 1);
    } else {
      setLineItems([]);
      setNextLineItemId(1);
    }
  };

  React.useEffect(() => {
    let ignoreInvoiceContext = false;
    const openingNow = open && !wasOpenRef.current;

    if (openingNow) {
      if (initialInvoice) {
        setStep('invoice');
        setHasGenerateAttempted(false);
        setInvoiceFeedback(null);
        setLiveInvoice(initialInvoice);
        populateInvoiceData(initialInvoice);

        // Fetch full invoice details from API
        if (initialInvoice.id) {
          setIsLoadingInvoice(true);
          getClientInvoice(initialInvoice.id)
            .then((fullInvoice) => {
              setLiveInvoice(fullInvoice);
              populateInvoiceData(fullInvoice);
            })
            .catch((error) => {
              console.error('Failed to fetch invoice details:', error);
            })
            .finally(() => {
              setIsLoadingInvoice(false);
            });
        }
      } else {
        setStep('details');
        setHasGenerateAttempted(false);
        setHasCreatedInvoice(false);
        setInvoiceFeedback(null);
        if (initialClientId) {
          setSelectedClientId(initialClientId);
        }
        if (initialClientSnapshot) {
          setSelectedClient(initialClientSnapshot);
          if (initialClientSnapshot.id) {
            setSelectedClientId(String(initialClientSnapshot.id));
          } else {
            setSelectedClientId('');
          }
        }
        // Reset fields when opening without initialInvoice
        setInvoiceNumber('');
        setIssueDate('');
        setDueDate('');
        setSubscriptionStartDate('');
        setSubscriptionEndDate('');
        loadedSubscriptionForClientRef.current = null;
        setInvoiceDueDays(null);
        setInvoiceCurrency(null);
        setInvoiceClinicLogoUrl('');
        setInvoiceShowClinicLogo(true);
        setInvoiceContextPlans([]);
        setInvoiceContextError(null);
        setTaxRate('');
        setDiscountAmountInput(DEFAULT_DISCOUNT);
        setNotes(DEFAULT_NOTES);
        setLineItems([]);
        setNextLineItemId(2);
        setLiveInvoice(null);
        setIsInvoiceContextLoading(true);
        setIsSubscriptionDefaultsLoading(Boolean(initialClientId || initialClientSnapshot?.id));
        const draftSnapshot = initialFormDraft ?? null;
        Promise.all([
          getNutritionistInvoiceContext(),
          getGeneratedClientInvoiceId(),
          getNutritionistConsultationTypes(),
        ])
          .then(([context, generatedInvoiceId, consultationTypes]) => {
            if (ignoreInvoiceContext || !openRef.current) return;
            const lineItemOptions = buildLineItemOptionsWithConsultationTypes(context, consultationTypes);
            applyInvoiceContextDefaults(context, lineItemOptions, {
              skipLineItems: Boolean(draftSnapshot),
            });
            const hasBillableClientOnOpen = Boolean(
              initialClientId || initialClientSnapshot?.id
            );
            if (!hasBillableClientOnOpen && !draftSnapshot?.subscriptionStartDate) {
              const defaultSubscriptionDates = getDefaultSubscriptionDates();
              setSubscriptionStartDate(defaultSubscriptionDates.start);
              setSubscriptionEndDate(defaultSubscriptionDates.end);
            }
            if (draftSnapshot) {
              const hasUserLineItems = lineItemsRef.current.some(
                (lineItem) => Boolean(lineItem.selectedPlanId) || lineItem.service.trim().length > 0
              );
              restoreFormDraft(
                {
                  ...draftSnapshot,
                  invoiceNumber: draftSnapshot.invoiceNumber || generatedInvoiceId.invoiceNo,
                },
                { preserveLineItems: hasUserLineItems }
              );
              if (draftSnapshot.subscriptionStartDate && draftSnapshot.subscriptionEndDate) {
                loadedSubscriptionForClientRef.current =
                  initialClientId ||
                  (initialClientSnapshot?.id ? String(initialClientSnapshot.id) : '') ||
                  selectedClientId ||
                  null;
                setIsSubscriptionDefaultsLoading(false);
              }
            } else {
              setInvoiceNumber(generatedInvoiceId.invoiceNo);
            }
          })
          .catch((error) => {
            if (ignoreInvoiceContext) return;
            console.error('Failed to fetch invoice context:', error);
            const maybeObj = error as {
              response?: { data?: { error?: string; message?: string } };
              message?: string;
            };
            const message =
              maybeObj?.response?.data?.error ||
              maybeObj?.response?.data?.message ||
              maybeObj?.message ||
              'Could not load invoice context';
            setInvoiceContextError(message);
            setInvoiceFeedback({
              variant: 'error',
              title: 'Could not load invoice context',
              message,
            });
          })
          .finally(() => {
            if (!ignoreInvoiceContext) {
              setIsInvoiceContextLoading(false);
            }
          });
      }
    }
    if (!open) {
      setLiveInvoice(null);
      setIsInvoiceContextLoading(false);
      setIsSubscriptionDefaultsLoading(false);
    }

    wasOpenRef.current = open;

    return () => {
      ignoreInvoiceContext = true;
    };
  }, [open, initialInvoice, initialClientId, initialClientSnapshot, initialFormDraft, applyInvoiceContextDefaults]);

  React.useEffect(() => {
    const clientId = selectedClientId.trim();
    if (!clientId || !open || initialInvoice) {
      if (!clientId || !open) {
        setIsSubscriptionDefaultsLoading(false);
      }
      return;
    }
    if (loadedSubscriptionForClientRef.current === clientId) {
      setIsSubscriptionDefaultsLoading(false);
      return;
    }

    let cancelled = false;
    loadedSubscriptionForClientRef.current = clientId;
    setIsSubscriptionDefaultsLoading(true);

    const applyDefaultSubscriptionDates = () => {
      const defaults = getDefaultSubscriptionDates();
      setSubscriptionStartDate(defaults.start);
      setSubscriptionEndDate(defaults.end);
    };

    void getClientById(clientId)
      .then((response) => {
        if (cancelled) return;
        const activeSubscription = response.activeSubscription;
        if (activeSubscription?.startDate && activeSubscription?.endDate) {
          setSubscriptionStartDate(formatInvoiceInputDate(activeSubscription.startDate));
          setSubscriptionEndDate(formatInvoiceInputDate(activeSubscription.endDate));
          return;
        }
        applyDefaultSubscriptionDates();
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to load client subscription defaults:', error);
        applyDefaultSubscriptionDates();
      })
      .finally(() => {
        if (!cancelled) {
          setIsSubscriptionDefaultsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedClientId, open, initialInvoice]);

  const isInvoiceDetailsInitialLoading =
    isInvoiceContextLoading || (Boolean(selectedClientId.trim()) && isSubscriptionDefaultsLoading);

  const selectedClientName = (
    selectedClient?.fullName?.trim() ||
    selectedClient?.name?.trim() ||
    `${selectedClient?.firstName ?? ''} ${selectedClient?.lastName ?? ''}`.trim()
  );
  const selectedClientLinkId = selectedClientId || initialClientId || initialInvoice?.clientId || '';
  const selectedClientEmail = selectedClient?.email?.trim() || '';
  const hasBillableClient = Boolean(
    selectedClientId || (isDeferredOnboardingFlow && selectedClientName)
  );
  const normalizedLineItems = useMemo(
    () =>
      lineItems.map((lineItem) => {
        const quantityValue = parseNumericValue(lineItem.quantity);
        const unitPriceValue = parseNumericValue(lineItem.unitPrice);

        return {
          ...lineItem,
          quantityValue,
          unitPriceValue,
          lineTotal: quantityValue * unitPriceValue,
        };
      }),
    [lineItems]
  );
  const subtotalAmount = useMemo(
    () => normalizedLineItems.reduce((acc, lineItem) => acc + lineItem.lineTotal, 0),
    [normalizedLineItems]
  );
  const taxRateValue = parseNumericValue(taxRate);
  const discountAmount = parseNumericValue(discountAmountInput);
  const taxAmount = subtotalAmount * (taxRateValue / 100);
  const totalAmount = Math.max(0, subtotalAmount + taxAmount - discountAmount);
  const taxRateOptions = useMemo(() => {
    const currentRate = taxRate.trim();
    if (!currentRate || DEFAULT_TAX_RATE_OPTIONS.includes(currentRate)) {
      return DEFAULT_TAX_RATE_OPTIONS;
    }

    return [...DEFAULT_TAX_RATE_OPTIONS, currentRate].sort(
      (a, b) => parseNumericValue(a) - parseNumericValue(b)
    );
  }, [taxRate]);
  const isCreateContextReady = Boolean(
    invoiceNumber.trim() &&
    invoiceDueDays !== null &&
    displayCurrency &&
    taxRate.trim() &&
    !isInvoiceContextLoading &&
    !invoiceContextError
  );
  const hasInvoicePlans = invoiceContextPlans.length > 0;
  const hasEmptyLineItem = lineItems.some((lineItem) => !lineItem.selectedPlanId);
  const hasCompleteLineItems =
    lineItems.length > 0 &&
    normalizedLineItems.every(
      (item) =>
        Boolean(item.selectedPlanId) &&
        item.service.trim().length > 0 &&
        item.quantityValue > 0 &&
        item.unitPrice.trim() !== '' &&
        item.unitPriceValue >= 0
    );
  const showClientRequiredMark = hasGenerateAttempted && !hasBillableClient;
  const showLineItemsRequiredMark = hasGenerateAttempted && !hasCompleteLineItems;
  const isSubscriptionRangeValid = useMemo(() => {
    if (!subscriptionStartDate || !subscriptionEndDate) return false;
    try {
      const start = parseInvoiceInputDate(subscriptionStartDate);
      const end = parseInvoiceInputDate(subscriptionEndDate);
      return end >= start;
    } catch {
      return false;
    }
  }, [subscriptionStartDate, subscriptionEndDate]);
  const showSubscriptionRequiredMark = hasGenerateAttempted && !isSubscriptionRangeValid;
  const subscriptionPeriodLabel = formatSubscriptionPeriodLabel(subscriptionStartDate, subscriptionEndDate);
  const selectedLineItemPlanIds = useMemo(
    () =>
      new Set(
        lineItems
          .map((lineItem) => lineItem.selectedPlanId)
          .filter((planId): planId is string => Boolean(planId))
      ),
    [lineItems]
  );
  const hasDuplicateSelectedLineItems = useMemo(() => {
    const selectedPlanIds = lineItems
      .map((lineItem) => lineItem.selectedPlanId)
      .filter((planId): planId is string => Boolean(planId));

    return selectedPlanIds.length !== new Set(selectedPlanIds).size;
  }, [lineItems]);
  const hasUnselectedInvoicePlans = useMemo(
    () => invoiceContextPlans.some((plan) => !selectedLineItemPlanIds.has(plan.id)),
    [invoiceContextPlans, selectedLineItemPlanIds]
  );
  const canAddLineItem =
    isCreateContextReady &&
    hasInvoicePlans &&
    lineItems.length < invoiceContextPlans.length &&
    hasUnselectedInvoicePlans;
  const showLineItemDetailColumns = hasInvoicePlans;
  const lineItemCatalogLabel = 'Service or Package';
  const lineItemSearchPlaceholder = 'Search consultations, programs, or packages';
  const emptyLineItemOptionsMessage = EMPTY_LINE_ITEM_OPTIONS_MESSAGE;
  const noMatchingLineItemOptionsMessage = 'No matching consultations, programs, or packages';
  const allLineItemOptionsUsedMessage =
    'All consultations, programs, and packages have been added. Increase QTY for repeats.';
  const duplicateLineItemSelectionMessage =
    'Each consultation, program, or package can only be added once. Increase QTY for repeat billing.';
  const emptyLineItemSelectionMessage = 'Add a consultation, program, or package to continue.';
  const invoiceDetailsStatusText = invoiceContextError
    ? invoiceContextError
    : isInvoiceContextLoading
      ? 'Loading invoice setup'
      : !hasInvoicePlans
        ? emptyLineItemOptionsMessage
        : isDeferredOnboardingFlow
          ? 'Select a consultation, program, or package to save this payment request'
          : 'Fill all required fields to continue';

  const buildInvoicePdfPayload = (): InvoicePdfPayload => {
    if (!invoiceNumber.trim()) {
      throw new Error('Invoice number is missing');
    }
    if (!displayCurrency) {
      throw new Error('Invoice currency is missing');
    }
    const issueParsed = parseInvoiceDisplayDate(issueDate);
    const dueParsed = parseInvoiceDisplayDate(dueDate);
    return {
      invoiceNo: invoiceNumber.trim(),
      currency: displayCurrency,
      clinicLogoUrl: currentClinicLogoUrl || null,
      fromName: fromName || '—',
      fromEmail: fromEmail || '—',
      billToName: selectedClientName || '—',
      billToEmail: selectedClientEmail || '—',
      issueDate: format(issueParsed, INVOICE_DISPLAY_DATE_FORMAT),
      dueDate: format(dueParsed, INVOICE_DISPLAY_DATE_FORMAT),
      lineItems: normalizedLineItems.map((li) => ({
        description: li.service || '—',
        quantity: li.quantityValue,
        unitPrice: li.unitPriceValue,
        lineTotal: li.lineTotal,
      })),
      subtotal: subtotalAmount,
      taxPct: taxRateValue,
      taxAmount,
      discount: discountAmount,
      total: totalAmount,
      status: invoiceRef?.status || '',
      notes: notes.trim() ? notes : null,
    };
  };

  const handleDownloadInvoicePdf = async () => {
    if (isInvoicePdfBusy) return;
    setIsInvoicePdfBusy(true);
    try {
      const payload = await withInvoiceClinicLogoDataUrl(invoiceRef?.id, buildInvoicePdfPayload());
      await downloadInvoicePdf(payload);
      setInvoiceFeedback({
        variant: 'success',
        title: 'Invoice PDF downloaded',
        message: 'The invoice PDF has been downloaded.',
      });
    } catch (error) {
      console.error('Invoice PDF export failed', error);
      setInvoiceFeedback({
        variant: 'error',
        title: 'Could not generate PDF',
        message: getApiErrorMessage(error),
      });
    } finally {
      setIsInvoicePdfBusy(false);
    }
  };

  const handlePrintInvoicePdf = async () => {
    if (isInvoicePdfBusy) return;
    setIsInvoicePdfBusy(true);
    try {
      const payload = await withInvoiceClinicLogoDataUrl(invoiceRef?.id, buildInvoicePdfPayload());
      await printInvoicePdf(payload);
    } catch (error) {
      console.error('Invoice print failed', error);
      setInvoiceFeedback({
        variant: 'error',
        title: 'Could not open print dialog',
        message: getApiErrorMessage(error),
      });
    } finally {
      setIsInvoicePdfBusy(false);
    }
  };

  const handleInlineStatusChange = (event: SelectChangeEvent<StatusSelectValue>) => {
    const next = event.target.value as StatusSelectValue;
    const current = normalizeStatusForSelect(invoiceRef?.status);
    if (next === current) return;
    if (next === 'paid' || next === 'discarded') {
      setPendingStatusChange(next);
      return;
    }
    void handleConfirmStatusChange(next);
  };

  const handleConfirmStatusChange = async (next: StatusSelectValue) => {
    const id = invoiceRef?.id;
    if (!id) return;
    setIsUpdatingInvoiceStatus(true);
    try {
      const updated = await updateInvoiceStatus(
        id,
        next === 'paid'
          ? {
              status: 'paid',
              paidAt: new Date().toISOString(),
              paymentMethod: LIST_MARK_PAID_PAYMENT_METHOD,
            }
          : { status: mapUiStatusToApiStatus(next) }
      );
      setLiveInvoice(updated);
      setPendingStatusChange(null);
      setInvoiceFeedback({
        variant: 'success',
        title: 'Invoice status updated',
        message: `Status changed to ${statusLabel(next)}.`,
      });
      if (!hasCreatedInvoice) {
        onInvoiceUpdated?.();
      }
    } catch (error) {
      console.error('Failed to update invoice status', error);
      setInvoiceFeedback({
        variant: 'error',
        title: 'Could not update invoice status',
        message: getApiErrorMessage(error),
      });
    } finally {
      setIsUpdatingInvoiceStatus(false);
    }
  };

  const getStepNumberBadgeSx = (active: boolean) =>
    ({
      width: 20,
      height: 20,
      borderRadius: '999px',
      bgcolor: active ? '#20AEDC' : '#F3F4F6',
      color: active ? '#fff' : '#99A1AF',
      fontSize: '12px',
      fontWeight: 700,
      lineHeight: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    } as const);

  const buildCreateInvoicePayload = (sourceLineItems: EditableLineItem[] = lineItemsRef.current): CreateInvoicePayload => {
    const issueDay = parseInvoiceDisplayDate(issueDate);
    const dueDay = addDays(issueDay, invoiceDueDays ?? 0);
    const subscriptionStartDay = parseInvoiceInputDate(subscriptionStartDate);
    const subscriptionEndDay = parseInvoiceInputDate(subscriptionEndDate);
    const payloadLineItems = sourceLineItems.map((lineItem) => {
      const quantityValue = parseNumericValue(lineItem.quantity);
      const unitPriceValue = parseNumericValue(lineItem.unitPrice);
      return {
        description: lineItem.service,
        qty: quantityValue || 0,
        unitPrice: unitPriceValue || 0,
      };
    });

    return {
      invoiceNo: invoiceNumber.trim(),
      nutritionistId: user?.id || '<nutritionistId>',
      clientId: selectedClientId || '<clientId>',
      lineItems: payloadLineItems,
      taxPct: taxRateValue || 0,
      discount: discountAmount || 0,
      currency: displayCurrency,
      issueDate: calendarDateToIsoForApi(issueDay),
      dueDate: calendarDateToIsoForApi(dueDay),
      subscriptionStartDate: calendarDateToIsoForApi(subscriptionStartDay),
      subscriptionEndDate: calendarDateToIsoForApi(subscriptionEndDay),
      sendEmail: sendByEmailEnabled,
      notes: notes || null,
    };
  };

  const handleConfirmAndCreate = async () => {
    if (!isCreateContextReady || !displayCurrency || invoiceDueDays === null) {
      setInvoiceFeedback({
        variant: 'error',
        title: 'Invoice context required',
        message: invoiceContextError || 'Invoice context is required before creating an invoice.',
      });
      return;
    }

    if (!hasInvoicePlans || normalizedLineItems.some((item) => !item.selectedPlanId)) {
      setInvoiceFeedback({
        variant: 'error',
        title: 'Line item required',
        message: isDeferredOnboardingFlow
          ? 'Select a consultation, program, or package before saving this payment request'
          : 'Select a consultation, program, or package before creating an invoice',
      });
      return;
    }

    if (hasDuplicateSelectedLineItems) {
      setInvoiceFeedback({
        variant: 'error',
        title: 'Duplicate line item',
        message: duplicateLineItemSelectionMessage,
      });
      return;
    }

    if (!hasBillableClient) {
      setInvoiceFeedback({
        variant: 'error',
        title: 'Client required',
        message: 'Select a client before creating an invoice.',
      });
      return;
    }

    if (!isSubscriptionRangeValid) {
      setInvoiceFeedback({
        variant: 'error',
        title: 'Subscription period required',
        message: 'Select a subscription start and end date. The end date must be on or after the start date.',
      });
      return;
    }

    setInvoiceFeedback(null);
    setIsCreating(true);
    try {
      const issueDay = parseInvoiceDisplayDate(issueDate);
      const dueDay = addDays(issueDay, invoiceDueDays);
      setDueDate(format(dueDay, INVOICE_DISPLAY_DATE_FORMAT));

      if (deferInvoiceCreation && !selectedClientId) {
        const formDraft = captureFormDraft();
        const payload = buildCreateInvoicePayload(lineItemsRef.current);
        onFormDraftSave?.(formDraft);
        await onInvoiceDraftSave?.(payload);
        setHasCreatedInvoice(true);
        setInvoiceFeedback({
          variant: 'success',
          title: 'Payment request saved',
          message: 'This payment request will be created when the client is added.',
        });
        handleClose();
        return;
      }

      const createdInvoice = await createClientInvoice(buildCreateInvoicePayload());
      if (createdInvoice) {
        setLiveInvoice(createdInvoice);
        populateInvoiceData(createdInvoice, { preserveClinicLogoUrl: true });
      }
      setHasCreatedInvoice(true);
      setStep('done');
    } catch (error) {
      console.error('Failed to create invoice', error);
      setInvoiceFeedback({
        variant: 'error',
        title: isDeferredOnboardingFlow ? 'Failed to save payment request' : 'Failed to create invoice',
        message: getApiErrorMessage(error),
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleGenerateInvoiceClick = () => {
    setHasGenerateAttempted(true);

    if (!hasBillableClient || !hasCompleteLineItems || !isSubscriptionRangeValid) {
      return;
    }

    setHasGenerateAttempted(false);

    if (isDeferredOnboardingFlow) {
      void handleConfirmAndCreate();
      return;
    }

    setStep('preview');
  };

  const handleClose = () => {
    if (step === 'invoice' && hasCreatedInvoice) {
      setStep('done');
      return;
    }

    if (isDeferredOnboardingFlow && onFormDraftSave) {
      onFormDraftSave(captureFormDraft());
    }

    setStep('details');
    setHasGenerateAttempted(false);
    setInvoiceFeedback(null);
    setSelectedClientId('');
    setSelectedClient(null);
    setSendByEmailEnabled(true);
    setInvoiceNumber('');
    setIssueDate('');
    setDueDate('');
    setSubscriptionStartDate('');
    setSubscriptionEndDate('');
    loadedSubscriptionForClientRef.current = null;
    setInvoiceDueDays(null);
    setInvoiceCurrency(null);
    setInvoiceClinicLogoUrl('');
    setInvoiceShowClinicLogo(true);
    setInvoiceContextPlans([]);
    setInvoiceContextError(null);
    setIsInvoiceContextLoading(false);
    setIsSubscriptionDefaultsLoading(false);
    setTaxRate('');
    setDiscountAmountInput(DEFAULT_DISCOUNT);
    setNotes(DEFAULT_NOTES);
    setLineItems([]);
    setNextLineItemId(2);
    setLiveInvoice(null);
    const shouldRefreshInvoices = hasCreatedInvoice;
    setHasCreatedInvoice(false);
    if (shouldRefreshInvoices) {
      onInvoiceUpdated?.();
    }
    onClose();
  };

  const handleLineItemQuantityChange = (lineItemId: number, value: string) => {
    setLineItems((currentLineItems) =>
      currentLineItems.map((lineItem) =>
        lineItem.id === lineItemId ? { ...lineItem, quantity: value } : lineItem
      )
    );
  };

  const handleLineItemUnitPriceChange = (lineItemId: number, value: string) => {
    setLineItems((currentLineItems) =>
      currentLineItems.map((lineItem) =>
        lineItem.id === lineItemId ? { ...lineItem, unitPrice: value } : lineItem
      )
    );
  };

  const handlePlanSearchTextChange = (lineItemId: number, value: string) => {
    setLineItems((currentLineItems) =>
      currentLineItems.map((lineItem) =>
        lineItem.id === lineItemId
          ? { ...lineItem, service: value, unitPrice: '', selectedPlanId: undefined }
          : lineItem
      )
    );
  };

  const handleApplyPlanToLineItem = (lineItemId: number, plan: InvoiceContextPlan) => {
    setLineItems((currentLineItems) =>
      currentLineItems.some((lineItem) => lineItem.id !== lineItemId && lineItem.selectedPlanId === plan.id)
        ? currentLineItems
        : currentLineItems.map((lineItem) =>
            lineItem.id === lineItemId ? createLineItemFromPlan(plan, lineItem.id) : lineItem
          )
    );
  };

  const handleAddLineItem = () => {
    if (!canAddLineItem) return;
    setLineItems((currentLineItems) => [
      ...currentLineItems,
      createEmptyLineItem(nextLineItemId),
    ]);
    setNextLineItemId((currentValue) => currentValue + 1);
  };

  const handleRemoveLineItem = (lineItemId: number) => {
    setLineItems((currentLineItems) => {
      const remainingLineItems = currentLineItems.filter((lineItem) => lineItem.id !== lineItemId);
      if (remainingLineItems.length > 0) return remainingLineItems;

      return hasInvoicePlans ? [createEmptyLineItem(nextLineItemId)] : [];
    });
    setNextLineItemId((currentValue) => currentValue + 1);
  };

  const invoiceStatusLower = (invoiceRef?.status || '').toLowerCase();
  const invoiceStatusValue = normalizeStatusForSelect(invoiceRef?.status);
  const handleOpenCreatedInvoiceView = () => {
    setStep('invoice');
    if (!liveInvoice?.id) return;
    setIsLoadingInvoice(true);
    getClientInvoice(liveInvoice.id)
      .then((fullInvoice) => {
        setLiveInvoice(fullInvoice);
        populateInvoiceData(fullInvoice);
      })
      .catch((error) => {
        console.error('Failed to fetch invoice details:', error);
      })
      .finally(() => {
        setIsLoadingInvoice(false);
      });
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        'data-demo-id': 'invoice-flow-dialog',
        sx: {
          width: {
            xs: 'calc(100vw - 16px)',
            sm: step === 'done' ? '480px' : step === 'invoice' ? '704px' : '720px',
          },
          maxWidth: {
            xs: 'calc(100vw - 16px)',
            sm: 'calc(100vw - 32px)',
          },
          maxHeight: {
            xs: 'calc(100dvh - 16px)',
            sm: 'calc(100dvh - 32px)',
          },
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '16px',
          overflow: 'hidden',
          m: { xs: 1, sm: 2 },
        },
      }}
    >
      <Box sx={{ bgcolor: '#fff', display: 'flex', flexDirection: 'column', maxHeight: 'inherit', minHeight: 0 }}>
        <Box sx={{ height: 65, minHeight: 65, px: { xs: 2, sm: 2.5 }, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Box sx={{ width: 28, height: 28, borderRadius: '10px', bgcolor: '#ebf9ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileText size={14} color="#20aedc" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: '#111827', fontWeight: 600, fontSize: '13px', lineHeight: '19.5px' }}>Invoice Details</Typography>
              <Typography sx={{ color: '#9ca3af', fontSize: '12px', lineHeight: '16.5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <ClientNameLink clientId={selectedClientLinkId} className="text-[#9ca3af] hover:text-[#6b7280]">
                  {selectedClientName}
                </ClientNameLink>
                {' . '}
                {isInvoiceDetailsInitialLoading && step === 'details' ? 'Loading...' : invoiceNumber || '—'}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleClose} sx={{ width: 28, height: 28, borderRadius: '10px', color: '#6b7280', flexShrink: 0 }}>
            <X size={15} />
          </IconButton>
        </Box>

        {!isDeferredOnboardingFlow && step !== 'done' && step !== 'invoice' && (
          <Box data-demo-id="invoice-flow-steps" sx={{ minHeight: 44, borderBottom: '1px solid #f3f4f6', px: { xs: 2, sm: 2.5 }, display: 'flex', alignItems: 'center', gap: 1, pb: '1px', overflowX: 'auto', flexShrink: 0, '&::-webkit-scrollbar': { display: 'none' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={getStepNumberBadgeSx(true)}>1</Box>
              <Typography sx={{ color: '#20aedc', fontSize: '12px', lineHeight: '16px', fontWeight: 600 }}>Invoice Details</Typography>
              <ChevronRight size={12} color="#99a1af" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={getStepNumberBadgeSx(step === 'preview')}>2</Box>
              <Typography sx={{ color: step === 'preview' ? '#20aedc' : '#99a1af', fontSize: '12px', lineHeight: '16px', fontWeight: step === 'preview' ? 600 : 400 }}>Preview</Typography>
              <ChevronRight size={12} color="#99a1af" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={getStepNumberBadgeSx(false)}>3</Box>
              <Typography sx={{ color: '#99a1af', fontSize: '12px', lineHeight: '16px', fontWeight: 400 }}>Done</Typography>
            </Box>
          </Box>
        )}

        {invoiceFeedback ? (
          <Box sx={{ px: 2.5, pt: 1.5 }}>
            <InlineFeedback
              variant={invoiceFeedback.variant}
              title={invoiceFeedback.title}
              message={invoiceFeedback.message}
            />
          </Box>
        ) : null}

        {step === 'details' && (
          isInvoiceDetailsInitialLoading ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1.5,
                height: { xs: 260, sm: 400 },
                px: 2.5,
                py: 4,
              }}
            >
              <CircularProgress size={32} sx={{ color: '#20aedc' }} />
              <Typography sx={{ color: '#6b7280', fontSize: '14px' }}>
                {isDeferredOnboardingFlow ? 'Loading payment request...' : 'Loading invoice...'}
              </Typography>
            </Box>
          ) : (
          <>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 240px' } }}>
                <Box sx={{ borderRight: { xs: 'none', md: '1px solid #f3f4f6' }, borderBottom: { xs: '1px solid #f3f4f6', md: 'none' }, p: { xs: 2, sm: 2.5 }, pb: 2, minWidth: 0 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                  <Box>
                    <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 600, letterSpacing: '0.6px', textTransform: 'uppercase', mb: 0.5 }}>Invoice #</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={invoiceNumber}
                      disabled
                      sx={filledDisabledTextFieldSx}
                      onChange={(event) => setInvoiceNumber(event.target.value)}
                    />
                  </Box>
                  <Box>
                    <Typography sx={invoiceFieldLabelSx}>
                      Client
                      {showClientRequiredMark ? <Box component="span" sx={invoiceRequiredMarkSx}>*</Box> : null}
                    </Typography>
                    {isDeferredOnboardingFlow && !selectedClientId ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={selectedClientName || '—'}
                        disabled
                        sx={filledDisabledTextFieldSx}
                      />
                    ) : (
                      <SearchableClientDropdown
                        value={selectedClientId || undefined}
                        disabled={isInvoiceMetaLocked}
                        onChange={(clientId, client) => {
                          const normalizedClientId = clientId?.trim() || '';
                          if (!normalizedClientId) {
                            setSelectedClientId('');
                            setSelectedClient(null);
                            return;
                          }
                          setSelectedClientId(normalizedClientId);
                          setSelectedClient(client ?? null);
                        }}
                        onEffectiveClientChange={setSelectedClient}
                        placeholder="Search clients"
                      />
                    )}
                  </Box>
                  <Box>
                    <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 600, letterSpacing: '0.6px', textTransform: 'uppercase', mb: 0.5 }}>Issue Date</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={issueDate}
                      disabled
                      sx={filledDisabledTextFieldSx}
                      onChange={(event) => setIssueDate(event.target.value)}
                    />
                  </Box>
                  <Box>
                    <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 600, letterSpacing: '0.6px', textTransform: 'uppercase', mb: 0.5 }}>Due Date</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={dueDate}
                      disabled
                      sx={filledDisabledTextFieldSx}
                      onChange={(event) => setDueDate(event.target.value)}
                    />
                  </Box>
                  <Box>
                    <Typography sx={invoiceFieldLabelSx}>
                      Subscription Start
                      {showSubscriptionRequiredMark ? <Box component="span" sx={invoiceRequiredMarkSx}>*</Box> : null}
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      value={subscriptionStartDate}
                      onChange={(event) => setSubscriptionStartDate(event.target.value)}
                      error={showSubscriptionRequiredMark && !subscriptionStartDate}
                      inputProps={{ 'aria-label': 'Subscription start date' }}
                    />
                  </Box>
                  <Box>
                    <Typography sx={invoiceFieldLabelSx}>
                      Subscription End
                      {showSubscriptionRequiredMark ? <Box component="span" sx={invoiceRequiredMarkSx}>*</Box> : null}
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      value={subscriptionEndDate}
                      onChange={(event) => setSubscriptionEndDate(event.target.value)}
                      error={showSubscriptionRequiredMark && (!subscriptionEndDate || !isSubscriptionRangeValid)}
                      helperText={
                        subscriptionEndDate && subscriptionStartDate && !isSubscriptionRangeValid
                          ? 'End date must be on or after start date'
                          : undefined
                      }
                      inputProps={{
                        min: subscriptionStartDate || undefined,
                        'aria-label': 'Subscription end date',
                      }}
                    />
                  </Box>
                  </Box>

                <Typography sx={{ ...invoiceFieldLabelSx, mt: 2 }}>
                  Line Items
                  {showLineItemsRequiredMark ? <Box component="span" sx={invoiceRequiredMarkSx}>*</Box> : null}
                </Typography>
                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflowX: 'auto', overflowY: 'hidden' }}>
                  <Box sx={{ minHeight: 34, px: 1.25, py: 0.75, bgcolor: '#f9fafb', borderBottom: '1px solid #f3f4f6', display: 'grid', gridTemplateColumns: showLineItemDetailColumns ? { xs: 'minmax(180px, 1fr) 56px 96px 32px', sm: 'minmax(0, 1fr) 48px 80px 28px' } : '1fr', alignItems: 'center', gap: 1, minWidth: showLineItemDetailColumns ? { xs: 430, sm: 'auto' } : 'auto' }}>
                    <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 600, lineHeight: '14px', textTransform: 'uppercase', whiteSpace: showLineItemDetailColumns ? { xs: 'normal', sm: 'nowrap' } : 'normal', overflow: showLineItemDetailColumns ? { sm: 'hidden' } : 'visible', textOverflow: showLineItemDetailColumns ? { sm: 'ellipsis' } : 'clip' }}>{lineItemCatalogLabel}</Typography>
                    {showLineItemDetailColumns ? (
                      <>
                        <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', textAlign: 'center' }}>Qty</Typography>
                        <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', textAlign: 'center' }}>Price</Typography>
                        <Box />
                      </>
                    ) : null}
                  </Box>
                  {lineItems.map((lineItem, index) => {
                    const selectedPlan =
                      lineItem.selectedPlanId
                        ? invoiceContextPlans.find((plan) => plan.id === lineItem.selectedPlanId) || null
                        : null;
                    const requiresManualPrice = selectedPlan?.totalAmount === null;
                    const lineItemOptions = invoiceContextPlans.filter(
                      (plan) =>
                        plan.id === lineItem.selectedPlanId ||
                        !selectedLineItemPlanIds.has(plan.id)
                    );

                    return (
                      <Box
                        key={lineItem.id}
                        sx={{
                          p: 1.25,
                          display: 'grid',
                          gridTemplateColumns: { xs: 'minmax(180px, 1fr) 56px 96px 32px', sm: 'minmax(0, 1fr) 48px 80px 28px' },
                          gap: 1,
                          alignItems: 'center',
                          borderTop: index > 0 ? '1px solid #f3f4f6' : 'none',
                          minWidth: { xs: 430, sm: 'auto' },
                        }}
                      >
                        <Autocomplete
                          size="small"
                          options={lineItemOptions}
                          loading={isInvoiceContextLoading}
                          disabled={!isCreateContextReady || !hasInvoicePlans}
                          value={selectedPlan}
                          inputValue={lineItem.service}
                          filterOptions={(options) => options}
                          noOptionsText={
                            hasUnselectedInvoicePlans ? noMatchingLineItemOptionsMessage : allLineItemOptionsUsedMessage
                          }
                          getOptionLabel={(option) => option.name}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          onInputChange={(_, value, reason) => {
                            if (reason === 'input' || reason === 'clear') {
                              handlePlanSearchTextChange(lineItem.id, value);
                            }
                          }}
                          onChange={(_, value) => {
                            if (value) {
                              handleApplyPlanToLineItem(lineItem.id, value);
                              return;
                            }
                            handlePlanSearchTextChange(lineItem.id, '');
                          }}
                          renderOption={(props, option) => (
                            <Box
                              component="li"
                              {...props}
                              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                            >
                              <Typography sx={{ color: '#111827', fontSize: '13px', fontWeight: 500, minWidth: 0 }}>
                                {option.name}
                              </Typography>
                              <Typography sx={{ color: option.totalAmount === null ? '#b45309' : '#6b7280', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                {formatPlanPriceLabel(option)}
                              </Typography>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={lineItemSearchPlaceholder}
                              sx={{
                                '& .MuiInputBase-input::placeholder': {
                                  fontSize: '13px',
                                },
                              }}
                            />
                          )}
                        />
                        <TextField
                          size="small"
                          value={lineItem.quantity}
                          disabled={!isCreateContextReady}
                          onChange={(event) => handleLineItemQuantityChange(lineItem.id, event.target.value)}
                        />
                        <TextField
                          size="small"
                          value={lineItem.unitPrice}
                          disabled={!isCreateContextReady || !requiresManualPrice}
                          placeholder={requiresManualPrice ? 'Price' : undefined}
                          onChange={(event) => handleLineItemUnitPriceChange(lineItem.id, event.target.value)}
                          InputProps={requiresManualPrice && displayCurrencyPrefix ? {
                            startAdornment: <Typography sx={{ mr: 0.5, color: '#6b7280', fontSize: '12px' }}>{displayCurrencyPrefix}</Typography>
                          } : undefined}
                          sx={!requiresManualPrice ? filledDisabledTextFieldSx : undefined}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveLineItem(lineItem.id)}
                          sx={{ opacity: lineItems.length > 1 ? 0.7 : 0.25 }}
                          disabled={lineItems.length === 1}
                        >
                          <Trash2 size={12} />
                        </IconButton>
                      </Box>
                    );
                  })}
                  {!isInvoiceContextLoading && !invoiceContextError && !hasInvoicePlans ? (
                    <Box sx={{ px: 1.25, py: 1.5, bgcolor: '#fff' }}>
                      <Typography sx={{ color: '#6b7280', fontSize: '12px', lineHeight: 1.5 }}>
                        {emptyLineItemOptionsMessage}
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
                {canAddLineItem ? (
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleAddLineItem}
                    sx={{ mt: 0.6, p: 0, minWidth: 0, color: '#20aedc', fontSize: '12px', textTransform: 'none' }}
                  >
                    + Add line item
                  </Button>
                ) : null}
                {!isInvoiceContextLoading && !invoiceContextError && hasInvoicePlans && hasEmptyLineItem ? (
                  <Typography sx={{ mt: 0.8, color: '#9ca3af', fontSize: '12px', lineHeight: 1.4 }}>
                    {emptyLineItemSelectionMessage}
                  </Typography>
                ) : null}

                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 600, letterSpacing: '0.6px', textTransform: 'uppercase', mb: 0.5 }}>Notes (optional)</Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    placeholder="Payment instructions or notes..."
                  />
                </Box>
              </Box>

              <Box sx={{ p: { xs: 2, sm: 2.5 }, pl: { xs: 2, md: 2 } }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                  <Box>
                    <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 600, letterSpacing: '0.6px', textTransform: 'uppercase', mb: 0.5 }}>Tax</Typography>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={taxRate}
                      disabled={isInvoiceContextLoading || Boolean(invoiceContextError)}
                      onChange={(event) => setTaxRate(event.target.value)}
                    >
                      {taxRateOptions.map((optionValue) => (
                        <MenuItem key={optionValue} value={optionValue}>
                          {formatRateLabel(parseNumericValue(optionValue))}%
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box>
                    <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 600, letterSpacing: '0.6px', textTransform: 'uppercase', mb: 0.5 }}>Discount</Typography>
                    <TextField
                      size="small"
                      fullWidth
                      value={discountAmountInput}
                      disabled={!isCreateContextReady}
                      onChange={(event) => setDiscountAmountInput(event.target.value)}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 0.5, color: '#6b7280', fontSize: '14px' }}>{displayCurrencyPrefix}</Typography>
                      }}
                    />
                  </Box>
                </Box>

                <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid rgba(32,174,220,0.2)', bgcolor: '#f8fffe', borderRadius: '10px' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography sx={{ color: '#6b7280', fontSize: '12px' }}>Subtotal</Typography><Typography sx={{ color: '#6b7280', fontSize: '12px' }}>{formatCurrency(subtotalAmount, displayCurrency)}</Typography></Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography sx={{ color: '#6b7280', fontSize: '12px' }}>Tax ({formatRateLabel(taxRateValue)}%)</Typography><Typography sx={{ color: '#6b7280', fontSize: '12px' }}>{formatCurrency(taxAmount, displayCurrency)}</Typography></Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.7 }}><Typography sx={{ color: '#6b7280', fontSize: '12px' }}>Discount</Typography><Typography sx={{ color: '#6b7280', fontSize: '12px' }}>-{formatCurrency(discountAmount, displayCurrency)}</Typography></Box>
                  <Box sx={{ borderTop: '1px solid rgba(32,174,220,0.2)', pt: 0.8, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: '#111827', fontSize: '13px', fontWeight: 600 }}>Total</Typography>
                    <Typography sx={{ color: '#20aedc', fontSize: '15px', fontWeight: 700 }}>{formatCurrency(totalAmount, displayCurrency)}</Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f9fafb', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Mail size={13} color="#20aedc" />
                    <Box>
                      <Typography sx={{ color: '#374151', fontSize: '12px', fontWeight: 500, lineHeight: '18px' }}>Send by email</Typography>
                      <Typography sx={{ color: '#9ca3af', fontSize: '12px', lineHeight: '15px' }}>
                        {sendByEmailEnabled ? `Notify ${selectedClientName}` : 'Email notifications disabled'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    role="switch"
                    aria-checked={sendByEmailEnabled}
                    onClick={() => setSendByEmailEnabled((prev) => !prev)}
                    sx={{
                      width: 32,
                      height: 18,
                      borderRadius: '999px',
                      bgcolor: sendByEmailEnabled ? '#20aedc' : '#d1d5db',
                      p: '2px',
                      display: 'flex',
                      justifyContent: sendByEmailEnabled ? 'flex-end' : 'flex-start',
                      alignItems: 'center',
                      cursor: 'pointer',
                      transition: 'background-color 160ms ease',
                    }}
                  >
                    <Box sx={{ width: 14, height: 14, borderRadius: '999px', bgcolor: '#fff', transition: 'all 160ms ease' }} />
                  </Box>
                </Box>
              </Box>
            </Box>
            </Box>

            <Box sx={{ minHeight: 61, borderTop: '1px solid #f3f4f6', px: { xs: 2, sm: 2.5 }, py: { xs: 1.5, sm: 0 }, display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, flexShrink: 0 }}>
              <Typography sx={{ color: invoiceContextError ? '#ea5455' : '#9ca3af', fontSize: '12px', width: { xs: '100%', sm: 'auto' } }}>{invoiceDetailsStatusText}</Typography>
              <Button
                data-demo-id="invoice-flow-preview"
                variant="contained"
                endIcon={isCreating ? <CircularProgress size={14} color="inherit" /> : <ArrowRight size={14} />}
                onClick={handleGenerateInvoiceClick}
                disabled={
                  isCreating ||
                  !isCreateContextReady ||
                  !hasInvoicePlans ||
                  !invoiceNumber.trim() ||
                  !issueDate ||
                  !dueDate ||
                  !isSubscriptionRangeValid ||
                  hasDuplicateSelectedLineItems
                }
                sx={{
                  textTransform: 'none',
                  borderRadius: '10px',
                  bgcolor: '#20aedc',
                  px: 2,
                  py: 1,
                  fontSize: '14px',
                  fontWeight: 600,
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': { bgcolor: '#1a9bc6' },
                  '&.Mui-disabled': {
                    bgcolor: '#e5e7eb',
                    color: '#9ca3af'
                  }
                }}
              >
                {isDeferredOnboardingFlow ? 'Save Payment Request' : 'Preview Invoice'}
              </Button>
            </Box>
          </>
          )
        )}

        {step === 'preview' && (
          <>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2 } }}>
              <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden' }}>
                <InvoiceLogoHeader clinicLogoUrl={currentClinicLogoUrl} />
                <Box sx={{ height: 79, px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundImage: 'linear-gradient(173deg, #20aedc 0%, #1690bf 100%)' }}>
                  <Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', letterSpacing: '0.8px', textTransform: 'uppercase' }}>Invoice</Typography>
                    <Typography sx={{ color: '#fff', fontSize: '20px', fontWeight: 700, lineHeight: '30px' }}>{invoiceNumber}</Typography>
                  </Box>
                </Box>

                <Box sx={{ p: 2.5, display: 'grid', gap: 2 }}>
                  <Box sx={{ pb: 1.5, borderBottom: '1px solid #f3f4f6', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
                    <Box>
                      <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase' }}>From</Typography>
                      <Typography sx={{ color: '#111827', fontSize: '12px', fontWeight: 600, mt: 0.25 }}>{fromName}</Typography>
                      <Typography sx={{ color: '#6b7280', fontSize: '12px', ...invoiceEmailTextSx }}>{fromEmail}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase' }}>Bill To</Typography>
                      <Typography sx={{ color: '#111827', fontSize: '12px', fontWeight: 600, mt: 0.25 }}>
                        <ClientNameLink clientId={selectedClientLinkId} className="text-[#111827] hover:text-[#111827]">
                          {selectedClientName}
                        </ClientNameLink>
                      </Typography>
                      <Typography sx={{ color: '#6b7280', fontSize: '12px', ...invoiceEmailTextSx }}>{selectedClientEmail}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase' }}>Issue Date</Typography>
                      <Typography sx={{ color: '#374151', fontSize: '12px', fontWeight: 500, mt: 0.25 }}>{issueDate}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase' }}>Due Date</Typography>
                      <Typography sx={{ color: '#374151', fontSize: '12px', fontWeight: 500, mt: 0.25 }}>{dueDate}</Typography>
                    </Box>
                  </Box>

                  {subscriptionStartDate && subscriptionEndDate ? (
                    <Box sx={{ pb: 1.5, borderBottom: '1px solid #f3f4f6' }}>
                      <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase' }}>Subscription Period</Typography>
                      <Typography sx={{ color: '#374151', fontSize: '12px', fontWeight: 500, mt: 0.25 }}>{subscriptionPeriodLabel}</Typography>
                    </Box>
                  ) : null}

                  <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '220px 72px 96px 96px', sm: 'minmax(220px, 319px) 1fr 1fr 1fr' }, borderBottom: '1px solid #f3f4f6', pb: 0.9, minWidth: { xs: 484, sm: 'auto' } }}>
                      <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', pl: 1 }}>Description</Typography>
                      <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', textAlign: 'center' }}>Qty</Typography>
                      <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', textAlign: 'center' }}>Price</Typography>
                      <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', textAlign: 'right', pr: 1 }}>Total</Typography>
                    </Box>
                    {normalizedLineItems.map((lineItem) => (
                      <Box key={lineItem.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '220px 72px 96px 96px', sm: 'minmax(220px, 319px) 1fr 1fr 1fr' }, borderBottom: '1px solid #f9fafb', py: 1.1, minWidth: { xs: 484, sm: 'auto' } }}>
                        <Typography sx={{ color: '#374151', fontSize: '12px', pl: 1 }}>{lineItem.service || '-'}</Typography>
                        <Typography sx={{ color: '#6b7280', fontSize: '12px', textAlign: 'center' }}>{lineItem.quantityValue}</Typography>
                        <Typography sx={{ color: '#6b7280', fontSize: '12px', textAlign: 'center' }}>{formatCurrency(lineItem.unitPriceValue, displayCurrency)}</Typography>
                        <Typography sx={{ color: '#111827', fontSize: '12px', fontWeight: 600, textAlign: 'right', pr: 1 }}>{formatCurrency(lineItem.lineTotal, displayCurrency)}</Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ px: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}><Typography sx={{ color: '#6b7280', fontSize: '12px' }}>Subtotal</Typography><Typography sx={{ color: '#6b7280', fontSize: '12px' }}>{formatCurrency(subtotalAmount, displayCurrency)}</Typography></Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}><Typography sx={{ color: '#6b7280', fontSize: '12px' }}>Tax ({formatRateLabel(taxRateValue)}%)</Typography><Typography sx={{ color: '#6b7280', fontSize: '12px' }}>{formatCurrency(taxAmount, displayCurrency)}</Typography></Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}><Typography sx={{ color: '#6b7280', fontSize: '12px' }}>Discount</Typography><Typography sx={{ color: '#6b7280', fontSize: '12px' }}>-{formatCurrency(discountAmount, displayCurrency)}</Typography></Box>
                    <Box sx={{ borderTop: '1px solid #e5e7eb', pt: 0.8, display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ color: '#111827', fontSize: '13px', fontWeight: 700 }}>Total</Typography>
                      <Typography sx={{ color: '#20aedc', fontSize: '15px', fontWeight: 700, lineHeight: '22.5px' }}>{formatCurrency(totalAmount, displayCurrency)}</Typography>
                    </Box>
                  </Box>

                  {notes.trim() ? (
                    <Box sx={{ bgcolor: 'rgba(239,246,255,0.6)', border: '1px solid #dbeafe', borderRadius: '14px', p: 2 }}>
                      <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Notes</Typography>
                      <Typography sx={{ color: '#4a5565', fontSize: '14px', mt: 0.5 }}>{notes.trim()}</Typography>
                    </Box>
                  ) : null}
                </Box>
              </Box>
            </Box>

            <Box sx={{ minHeight: 71, borderTop: '1px solid #f3f4f6', px: { xs: 2, sm: 2.5 }, py: 1.5, display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' }, flexShrink: 0 }}>
              <Button
                variant="outlined"
                onClick={() => setStep('details')}
                sx={{
                  textTransform: 'none',
                  borderRadius: '10px',
                  borderColor: '#e5e7eb',
                  color: '#6a7282',
                  px: 2.1,
                  py: 1,
                  width: { xs: '100%', sm: 'auto' },
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: '#d9dee6', bgcolor: '#f8fafc' },
                  '&:active': { transform: 'scale(0.98)', bgcolor: '#f1f5f9' },
                }}
              >
                <Typography component="span" sx={{ fontSize: '14px', mr: 0.6, lineHeight: 1 }}>{String.fromCharCode(8592)}</Typography>
                Back
              </Button>
              <Button
                data-demo-id="invoice-flow-confirm"
                variant="contained"
                endIcon={isCreating ? <CircularProgress size={14} color="inherit" /> : <ArrowRight size={14} />}
                onClick={handleConfirmAndCreate}
                disabled={isCreating}
                sx={{
                  textTransform: 'none',
                  borderRadius: '10px',
                  bgcolor: '#20aedc',
                  px: 2.6,
                  py: 1,
                  fontSize: '14px',
                  fontWeight: 600,
                  minWidth: { xs: 0, sm: 214 },
                  width: { xs: '100%', sm: 'auto' },
                  transition: 'all 0.2s',
                  '&:hover': { bgcolor: '#1a9bc6' },
                  '&:active': { transform: 'scale(0.98)' },
                }}
              >
                Confirm and Create
              </Button>
            </Box>
          </>
        )}

        {step === 'done' && (
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 2, sm: 2.5 }, pt: { xs: 5, sm: 8 }, pb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', width: '100%', maxWidth: 384 }}>
              <Box sx={{ width: 64, height: 64, borderRadius: '999px', bgcolor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <CheckCircle2 size={32} color="#16a34a" />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', width: '100%' }}>
                <Typography sx={{ color: '#111827', fontSize: '18px', fontWeight: 700, lineHeight: '27px', fontFamily: '"Inter", sans-serif' }}>
                  {isDeferredOnboardingFlow && !liveInvoice?.id ? 'Invoice ready' : 'Invoice Created!'}
                </Typography>
                <Typography sx={{ color: '#6b7280', fontSize: '13px', lineHeight: '19.5px', textAlign: 'center', fontFamily: '"Inter", sans-serif', fontWeight: 400 }}>
                  <Box component="span" sx={{ color: '#20aedc', fontWeight: 600 }}>{invoiceNumber}</Box>
                  {isDeferredOnboardingFlow && !liveInvoice?.id
                    ? ` will be created for ${selectedClientName || 'this client'} when you add the client.`
                    : ` has been added to ${selectedClientName}'s account.`}
                </Typography>
                {!(isDeferredOnboardingFlow && !liveInvoice?.id) ? (
                  <Typography sx={{ color: '#9ca3af', fontSize: '12px', lineHeight: '16.5px', textAlign: 'center', fontFamily: '"Inter", sans-serif', fontWeight: 400 }}>
                    A confirmation email has been sent to {selectedClientEmail}
                  </Typography>
                ) : null}
              </Box>

              <Box sx={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <Button
                  variant="outlined"
                  startIcon={<Eye size={13} />}
                  onClick={handleOpenCreatedInvoiceView}
                  sx={{ height: 42, borderRadius: '14px', borderColor: '#e5e7eb', color: '#4a5565', textTransform: 'none', fontSize: '12px', fontWeight: 500, lineHeight: '20px', fontFamily: '"Inter", sans-serif', transition: 'all 0.2s', '&:hover': { bgcolor: '#f8fafc', borderColor: '#d1d5db' }, '&:active': { bgcolor: '#f1f5f9', transform: 'scale(0.98)' } }}
                >
                  View Invoice
                </Button>
                <Button
                  variant="outlined"
                  startIcon={isInvoicePdfBusy ? <CircularProgress size={14} color="inherit" /> : <Download size={13} />}
                  disabled={isInvoicePdfBusy}
                  onClick={() => void handleDownloadInvoicePdf()}
                  sx={{ height: 42, borderRadius: '14px', borderColor: '#e5e7eb', color: '#4a5565', textTransform: 'none', fontSize: '12px', fontWeight: 500, lineHeight: '20px', fontFamily: '"Inter", sans-serif', transition: 'all 0.2s', '&:hover': { bgcolor: '#f8fafc', borderColor: '#d1d5db' }, '&:active': { bgcolor: '#f1f5f9', transform: 'scale(0.98)' } }}
                >
                  Download PDF
                </Button>
              </Box>
            </Box>

            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                mt: { xs: '40px', sm: '70px' },
                width: { xs: '100%', sm: 131 },
                height: 38,
                borderRadius: '14px',
                textTransform: 'none',
                bgcolor: '#20aedc',
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: '"Inter", sans-serif',
                transition: 'all 0.2s',
                '&:hover': { bgcolor: '#1a9bc6' },
                '&:active': { transform: 'scale(0.98)' },
              }}
            >
              Done
            </Button>
          </Box>
        )}

        {step === 'invoice' && (
          isLoadingInvoice ? (
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <CircularProgress />
            </Box>
          ) : (
          <>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.5 }, pb: 2, display: 'grid', gap: 2 }}>
              <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden' }}>
                <InvoiceLogoHeader clinicLogoUrl={currentClinicLogoUrl} />
                <Box sx={{ height: 80, px: 3, pt: 2.5, display: 'flex', justifyContent: 'space-between', backgroundImage: 'linear-gradient(171deg, #20aedc 0%, #1a8fb8 60%, #0e6a8a 100%)' }}>
                  <Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase' }}>Invoice</Typography>
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '24px', lineHeight: '32px' }}>{invoiceNumber}</Typography>
                  </Box>
                </Box>

                <Box sx={{ p: 3, display: 'grid', gap: 2.5 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))' }, gap: 3 }}>
                    <Box>
                      <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>From</Typography>
                      <Typography sx={{ color: '#1e2939', fontWeight: 600, fontSize: '14px', mt: 0.5 }}>{fromName}</Typography>
                      <Typography sx={{ color: '#6a7282', fontSize: '12px', ...invoiceEmailTextSx }}>{fromEmail}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Bill To</Typography>
                      <Typography sx={{ color: '#1e2939', fontWeight: 600, fontSize: '14px', mt: 0.5 }}>
                        <ClientNameLink clientId={selectedClientLinkId} className="text-[#1e2939] hover:text-[#1e2939]">
                          {selectedClientName}
                        </ClientNameLink>
                      </Typography>
                      <Typography sx={{ color: '#6a7282', fontSize: '12px', ...invoiceEmailTextSx }}>{selectedClientEmail}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Issue Date</Typography>
                      <Typography sx={{ color: '#364153', fontSize: '14px', mt: 0.8 }}>{issueDate}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Due Date</Typography>
                      <Typography sx={{ color: '#364153', fontSize: '14px', mt: 0.8 }}>{dueDate}</Typography>
                    </Box>
                  </Box>

                  {subscriptionStartDate && subscriptionEndDate ? (
                    <Box>
                      <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Subscription Period</Typography>
                      <Typography sx={{ color: '#364153', fontSize: '14px', mt: 0.8 }}>{subscriptionPeriodLabel}</Typography>
                    </Box>
                  ) : null}

                  <Box sx={{ borderTop: '1px solid #f3f4f6' }} />

                  <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '220px 72px 96px 96px', sm: '1.5fr 0.5fr 0.8fr 0.8fr' }, borderBottom: '1px solid #f3f4f6', pb: 1, minWidth: { xs: 484, sm: 'auto' } }}>
                      <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Description</Typography>
                      <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'center' }}>Qty</Typography>
                      <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'right' }}>Unit Price</Typography>
                      <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'right' }}>Total</Typography>
                    </Box>
                    {normalizedLineItems.map((lineItem) => (
                      <Box key={lineItem.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '220px 72px 96px 96px', sm: '1.5fr 0.5fr 0.8fr 0.8fr' }, borderBottom: '1px solid #f9fafb', py: 1.6, minWidth: { xs: 484, sm: 'auto' } }}>
                        <Typography sx={{ color: '#364153', fontSize: '14px' }}>{lineItem.service || '-'}</Typography>
                        <Typography sx={{ color: '#4a5565', fontSize: '14px', textAlign: 'center' }}>{lineItem.quantityValue}</Typography>
                        <Typography sx={{ color: '#4a5565', fontSize: '14px', textAlign: 'right' }}>{formatCurrency(lineItem.unitPriceValue, displayCurrency)}</Typography>
                        <Typography sx={{ color: '#364153', fontWeight: 500, fontSize: '14px', textAlign: 'right' }}>{formatCurrency(lineItem.lineTotal, displayCurrency)}</Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ bgcolor: '#f9fafb', borderRadius: '14px', p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}><Typography sx={{ color: '#6a7282', fontSize: '14px' }}>Subtotal</Typography><Typography sx={{ color: '#6a7282', fontSize: '14px' }}>{formatCurrency(subtotalAmount, displayCurrency)}</Typography></Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}><Typography sx={{ color: '#6a7282', fontSize: '14px' }}>Tax ({formatRateLabel(taxRateValue)}%)</Typography><Typography sx={{ color: '#6a7282', fontSize: '14px' }}>{formatCurrency(taxAmount, displayCurrency)}</Typography></Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}><Typography sx={{ color: '#6a7282', fontSize: '14px' }}>Discount</Typography><Typography sx={{ color: '#6a7282', fontSize: '14px' }}>-{formatCurrency(discountAmount, displayCurrency)}</Typography></Box>
                    <Box sx={{ borderTop: '1px solid #e5e7eb', pt: 1, display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ color: '#1e2939', fontWeight: 700, fontSize: '14px' }}>Total</Typography>
                      <Typography sx={{ color: '#20aedc', fontWeight: 700, fontSize: '16px' }}>{formatCurrency(totalAmount, displayCurrency)}</Typography>
                    </Box>
                  </Box>

                  {notes ? (
                    <Box sx={{ bgcolor: 'rgba(239,246,255,0.6)', border: '1px solid #dbeafe', borderRadius: '14px', p: 2 }}>
                      <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Notes</Typography>
                      <Typography sx={{ color: '#4a5565', fontSize: '14px', mt: 0.5 }}>{notes}</Typography>
                    </Box>
                  ) : null}
                </Box>
              </Box>
            </Box>

            <Box sx={{ borderTop: '1px solid #f3f4f6', px: { xs: 2, sm: '20px' }, py: '11px', display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' }, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
                <Button
                  variant="outlined"
                  startIcon={isResendingEmail ? <CircularProgress size={14} color="inherit" /> : <Send size={14} />}
                  onClick={handleResendEmail}
                  sx={{ height: 38, borderRadius: '14px', textTransform: 'none', borderColor: '#e5e7eb', color: '#4a5565', fontFamily: '"Inter", sans-serif', fontWeight: 500, fontSize: '14px', letterSpacing: '-0.1504px', px: '12px', flex: { xs: '1 1 100%', sm: '0 0 auto' }, '& .MuiButton-startIcon': { mr: '6px' }, transition: 'all 0.2s', '&:hover': { bgcolor: '#f8fafc', borderColor: '#d1d5db' }, '&:active': { bgcolor: '#f1f5f9', transform: 'scale(0.98)' } }}
                >
                  Send Email
                </Button>
                <Button
                  variant="outlined"
                  startIcon={isInvoicePdfBusy ? <CircularProgress size={14} color="inherit" /> : <Download size={14} />}
                  disabled={isInvoicePdfBusy}
                  onClick={() => void handleDownloadInvoicePdf()}
                  sx={{ height: 38, borderRadius: '14px', textTransform: 'none', borderColor: '#e5e7eb', color: '#4a5565', fontFamily: '"Inter", sans-serif', fontWeight: 500, fontSize: '14px', letterSpacing: '-0.1504px', px: '12px', flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' }, '& .MuiButton-startIcon': { mr: '6px' }, transition: 'all 0.2s', '&:hover': { bgcolor: '#f8fafc', borderColor: '#d1d5db' }, '&:active': { bgcolor: '#f1f5f9', transform: 'scale(0.98)' } }}
                >
                  Download
                </Button>
                <Button
                  variant="outlined"
                  startIcon={isInvoicePdfBusy ? <CircularProgress size={14} color="inherit" /> : <Printer size={14} />}
                  disabled={isInvoicePdfBusy}
                  onClick={() => void handlePrintInvoicePdf()}
                  sx={{ height: 38, borderRadius: '14px', textTransform: 'none', borderColor: '#e5e7eb', color: '#4a5565', fontFamily: '"Inter", sans-serif', fontWeight: 500, fontSize: '14px', letterSpacing: '-0.1504px', px: '12px', flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' }, '& .MuiButton-startIcon': { mr: '6px' }, transition: 'all 0.2s', '&:hover': { bgcolor: '#f8fafc', borderColor: '#d1d5db' }, '&:active': { bgcolor: '#f1f5f9', transform: 'scale(0.98)' } }}
                >
                  Print
                </Button>
              </Box>
              <FormControl size="small" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <Select<StatusSelectValue>
                  value={invoiceStatusValue}
                  onChange={(e) => handleInlineStatusChange(e)}
                  sx={{
                    minWidth: { xs: '100%', sm: 170 },
                    height: 38,
                    borderRadius: '14px',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                    '& .MuiSelect-select': { py: '8px', pl: 1.5, pr: 3 },
                  }}
                  renderValue={(selected) => (
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: statusColor(selected), letterSpacing: '-0.15px' }}>
                      {isUpdatingInvoiceStatus ? 'Updating...' : statusLabel(selected)}
                    </Typography>
                  )}
                >
                  <MenuItem value="due">Due</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="discarded">Discarded</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </>
          )
        )}
      </Box>
    </Dialog>
    <InvoiceStatusConfirmDialog
      open={Boolean(pendingStatusChange)}
      targetStatus={pendingStatusChange === 'paid' ? 'paid' : pendingStatusChange === 'discarded' ? 'discarded' : null}
      isSubmitting={isUpdatingInvoiceStatus}
      onCancel={() => setPendingStatusChange(null)}
      onConfirm={() => {
        if (pendingStatusChange) {
          void handleConfirmStatusChange(pendingStatusChange);
        }
      }}
    />
    </>
  );
};

export default CreateInvoiceFlowDialog;
